import type { Customer } from './types';
import { limitState, myChip } from './view';

// Mint-Customers-iPad-Detail (khung 1180): hồ sơ khách 2 cột. Dữ liệu dựng từ dòng khách như script board
// (`_d21` + `cfg` trong Mint-Customers-iPad). Tiền ẩn: KPI chỉ trạng thái (board `money=hide`).

export interface PadKpi { k: string; v: string; err?: boolean }
export function padKpis(c: Customer): PadKpi[] {
  const ls = limitState(c);
  return [
    { k: 'Job 90 ngày', v: String(c.jobs) },
    { k: 'Đang chạy', v: String(Math.max(1, Math.round(c.jobs / 5))) },
    { k: 'Hạn mức', v: ls === 'prepaid' ? 'Trả trước' : ls === 'over' ? 'Vượt' : 'Trong mức', err: ls === 'over' },
    { k: 'Quá hạn', v: c.credit.overdue > 0 ? 'Có' : '—', err: c.credit.overdue > 0 },
  ];
}

export const myText = (c: Customer): string => {
  const m = myChip(c.my);
  return m.tone === 'ok' ? `${m.text} user` : m.text;
};

export const padLegal = (c: Customer, prov: string, cls: number): [string, string][] => {
  const sao = c.id === 'SAVITA';
  return [
    ['Tên', sao ? 'CÔNG TY TNHH NÔNG SẢN SAO VIỆT' : `Công ty ${c.id}`],
    [c.kind === 'Cá nhân' ? 'Taxcode' : 'MST', c.mst],
    ['Địa chỉ', sao ? 'KCN Khánh An, U Minh, Cà Mau' : 'Đã tra từ cổng thuế'],
    ['Tỉnh · hạng', `${prov} · Hạng ${cls}`],
    ['Tình trạng', 'Đang hoạt động'],
  ];
};

export interface PadContact { name: string; role: string; phone: string; roles: string[] }
const CONTACTS: PadContact[] = [
  { name: 'Phạm Ngọc Thảo', role: 'Logistics', phone: '0909 ••• 203', roles: ['BKCF', 'B/L'] },
  { name: 'Trần Thị Mai Anh', role: 'Kế toán trưởng', phone: '0918 ••• 760', roles: ['Debit', 'Hoá đơn'] },
  { name: 'Nguyễn Hoàng Phúc', role: 'Chứng từ XNK', phone: '0937 ••• 905', roles: ['B/L', 'AN'] },
];
export const padContacts = (c: Customer): PadContact[] => (c.contactCount === 0 ? [] : CONTACTS);

export interface PadTodo { k: string; who: string; due: string; tone: 'warn' | 'err'; act: string }
export function padTodos(c: Customer): PadTodo[] {
  const out: PadTodo[] = [];
  if (padContacts(c).length === 0) out.push({ k: 'Thêm người nhận chứng từ', who: c.sale, due: 'trước job đầu', tone: 'warn', act: '+ Liên hệ' });
  if (limitState(c) === 'over') out.push({ k: 'Vượt hạn mức · giữ DO', who: c.sale, due: 'Hôm nay', tone: 'err', act: 'Công nợ' });
  if (c.my === 'wait') out.push({ k: 'Tài khoản myHANA chờ BOD duyệt', who: 'PHILIP', due: '[BOD-DIR]', tone: 'warn', act: 'Mở' });
  return out;
}

export function padInfo(c: Customer, prov: string, cls: number): [string, string][] {
  const legal = padLegal(c, prov, cls).filter(([k]) => !/MST|Taxcode|Tên/.test(k)).slice(0, 3);
  return [['ShortName', c.id], ['MST', c.mst], ['myHANA', myText(c)], ...legal, ['Điều khoản', c.credit.limit > 0 ? '30 ngày sau hoá đơn' : 'Trả trước']];
}

/** Dữ liệu CUSDATA minh hoạ của board (mpicCus): Sale · CS · Sale Asst · Kế toán theo khách. */
const PIC: Record<string, { cs: string; asst: string; acc: string }> = {
  SAVITA: { cs: 'NGOCNTB', asst: '', acc: 'LIENNTK' },
  NAMMY: { cs: 'NGOCNTB', asst: '', acc: 'LIENNTK' },
  GREENBUD: { cs: 'MAITTN', asst: 'AnNPH', acc: 'LIENNTK' },
};
export interface PadPic { who: string; none: boolean; role: string }
export function padPeople(c: Customer): PadPic[] {
  const base = PIC[c.id] ?? { cs: 'MAITTN', asst: 'AnNPH', acc: 'LIENNTK' };
  const parts = (c.split || `${c.sale} 100%`).split('·').map((x) => x.trim()).filter(Boolean).map((x) => {
    const m = x.match(/^(\S+)\s*(\d+%)?$/);
    return [m?.[1] ?? x, m?.[2] ?? ''] as const;
  });
  const rows: { who: string; roles: string[] }[] = [];
  const add = (role: string, who: string) => {
    const u = who.trim();
    const same = u && rows.find((r) => r.who.toUpperCase() === u.toUpperCase());
    if (same) { if (!same.roles.includes(role)) same.roles.push(role); return; }
    rows.push({ who: u, roles: [role] });
  };
  add('Sale', parts[0]?.[0] ?? c.sale);
  add('CS', base.cs);
  add('Sale Asst', base.asst);
  add('Kế toán', base.acc);
  parts.slice(1).forEach(([u, p]) => add(`Sale${p ? ` · ${p}` : ''}`, u));
  return rows.map((r) => ({ who: r.who || 'Chưa giao', none: !r.who, role: r.roles.join(' · ') }));
}

export const PAD_TABS = ['Tổng quan', 'Job', 'Công nợ', 'Tương tác', 'myHANA', 'Refund'] as const;
export const PAD_LINKS = ['Khách 360 đầy đủ', 'Job của khách', 'Cơ hội bán hàng', 'Công nợ · SOA'] as const;
